/**
 * ui#727: recovers the sender's TITLE from the single `message` field the transfer API carries, so the
 * recipient card can render a heading and a body instead of one undifferentiated paragraph.
 *
 * The create panel collects a Title and a Message separately and `home/composeMessage.ts` folds them
 * into one string with a blank line between them — the API has no second field. The recipient card then
 * rendered that whole string in one `<p>` at body weight, which is why the owner's review (2026-08-08)
 * read the title and the body as the same thing: with the shipped prefill they literally are, "Files for
 * you\n\nHi! Here are the files.".
 *
 * This is the inverse of that fold, and it lives here — React-free (epic #26) — because it is a RULE
 * about a wire format, not a rendering concern: it has to stay in step with `composeMessage`, and a
 * rule trapped in JSX cannot be asserted against literals.
 *
 * The inverse is deliberately PARTIAL, and it is not total: `composeMessage` DROPS empty parts, so a
 * title-only transfer, a message-only transfer and a real title+message can all produce strings this
 * side cannot tell apart. Where the shape is unreadable — no blank line at all, nothing after it, a
 * multi-line first block, or one past {@link TRANSFER_HEADING_MAX_CHARS} — it falls back to "all body",
 * which is what the page rendered before this existed.
 *
 * Where the shape is merely AMBIGUOUS it resolves toward a heading, and that is a deliberate trade
 * rather than an oversight. A sender who clears the prefilled Title and writes a message with
 * paragraphs will have their opening line set as the heading; nothing distinguishes that string from a
 * genuine title + body, and no guard here can recover the difference. The trade is worth taking because
 * the create panel prefills BOTH fields, so title + message is the shape almost every transfer actually
 * has — and the cost when it is wrong is bounded: the sender's own opening line, in their own order, one
 * step larger. The alternative loses the heading for every correctly-composed transfer to protect a
 * deliberately-emptied field.
 *
 * `transferMessage.test.ts` pins both halves, so the trade cannot be quietly reversed.
 */

/**
 * The longest first block that may be promoted to a heading.
 *
 * A bound is needed because the create form enforces none, so a sender can put an entire paragraph in
 * the Title field. Sized to the card, not to taste: the heading renders at 17px in the card's text
 * column, so 90 is a title that wraps and still reads as a title. Beyond that it is prose, and prose set
 * at heading weight is worse than prose.
 *
 * The column narrowed from ~470px to ~360px when the card took the create card's measure
 * (kitecorp/transferify-ui#1922), so a 90-character title now wraps onto a third line rather than a
 * second. The bound is left where the owner set it — whether a shorter one reads better is a copy
 * decision, not a consequence of moving the box — but the measurement is stated honestly here so the
 * next reader is not sizing against a column that no longer exists.
 */
export const TRANSFER_HEADING_MAX_CHARS = 90;

/** A sender's message, split into the two roles the card renders it in. */
export interface TransferMessageParts {
	/** The sender's title, or `null` when the message carries nothing that can honestly be one. */
	heading: string | null;
	/** Everything else, already trimmed. Empty only when the whole message was empty. */
	body: string;
}

/** Matches the separator `composeMessage` writes — a blank line — tolerating CRLF and stray spaces. */
const BLANK_LINE = /\r?\n[ \t]*\r?\n/;

/**
 * Splits a composed transfer message into its heading and body.
 *
 * Returns `heading: null` — meaning "render it all as body, as before" — for exactly four shapes, and
 * they are the UNREADABLE ones rather than the merely ambiguous ones (see the module note): no blank
 * line at all (one part, and which part is unknowable), nothing after the blank line (a title with no
 * body is just a message), a multi-line first block, or a first block past
 * {@link TRANSFER_HEADING_MAX_CHARS}. Anything else gets the heading.
 */
export function splitTransferMessage(message: string): TransferMessageParts {
	const trimmed = message.trim();
	const separator = BLANK_LINE.exec(trimmed);
	if (separator === null) {
		return {heading: null, body: trimmed};
	}
	const heading = trimmed.slice(0, separator.index).trim();
	const body = trimmed.slice(separator.index + separator[0].length).trim();
	if (heading === "" || body === "" || heading.includes("\n") || heading.length > TRANSFER_HEADING_MAX_CHARS) {
		return {heading: null, body: trimmed};
	}
	return {heading, body};
}
